// Spec §70 photo gallery viewer. Read-only on the web (uploads live in
// the mobile app for now — same restriction as the editor).
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { photosApi, reportsApi } from '@/api/reportsApi';
import { ApiException } from '@/api/errors';
import { formatDate } from '@/utils/format';
import type { ReportJson, ReportPhotoJson } from '@/api/types';
import { isVisiblePhoto, photoStatusLabel, photoUiStatusFor } from './photoStatusLabel';

export function PhotosScreen() {
  const { id = '' } = useParams<{ id: string }>();
  const { getToken } = useAuth();
  const [report, setReport] = useState<ReportJson | null>(null);
  const [photos, setPhotos] = useState<ReportPhotoJson[]>([]);
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    async function load() {
      setBusy(true);
      try {
        const [row, list] = await Promise.all([
          reportsApi.get(getToken, id),
          photosApi.list(getToken, id),
        ]);
        if (cancelled) return;
        setReport(row);
        // Hide soft-deleted tombstones so the gallery matches Mobile's
        // `watchPhotos` filter and never shows a deleted row. The
        // backend intentionally returns tombstones for reconciliation;
        // we filter at the presentation layer.
        setPhotos((list ?? []).filter(isVisiblePhoto));
      } catch (e) {
        if (cancelled) return;
        const err = e instanceof ApiException ? e : null;
        setError(err?.message ?? 'Could not load photos.');
      } finally {
        if (!cancelled) setBusy(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [id, getToken]);

  useEffect(() => {
    let cancelled = false;
    const created: string[] = [];
    async function resolve() {
      const next: Record<string, string> = {};
      for (const photo of photos) {
        if (!photo.thumbnail_r2_key) continue;
        try {
          const url = await photosApi.fetchAsset(
            getToken,
            photosApi.thumbnailUrl(photo.report_id, photo.id),
          );
          if (cancelled) {
            URL.revokeObjectURL(url);
            continue;
          }
          next[photo.id] = url;
          created.push(url);
        } catch {
          // ignore — placeholder
        }
      }
      if (!cancelled) setThumbnailUrls(next);
    }
    void resolve();
    return () => {
      cancelled = true;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [photos, getToken]);

  if (busy) {
    return <div className="empty">Loading photos…</div>;
  }

  return (
    <div className="stack">
      <div className="card">
        <div className="row" style={{ justifyContent: 'space-between' }}>
          <div>
            <h2 style={{ margin: 0 }}>{report?.title ? report.title : 'Photos'}</h2>
            <div className="muted" style={{ fontSize: 13 }}>
              {photos.length} photo{photos.length === 1 ? '' : 's'} · updated{' '}
              {formatDate(report?.updated_at ?? null)}
            </div>
          </div>
          <Link to={`/reports/${id}`} className="btn">
            Back to report
          </Link>
        </div>
        {error ? <div className="error-banner">{error}</div> : null}
        {photos.length === 0 ? (
          <div className="empty">No photos attached to this report yet.</div>
        ) : (
          <div className="photo-grid">
            {photos.map((photo) => (
              <div key={photo.id} className="photo-tile">
                {thumbnailUrls[photo.id] ? (
                  <img src={thumbnailUrls[photo.id]} alt="Report photo" />
                ) : (
                  <div className="empty" style={{ padding: 8, fontSize: 12 }}>
                    {photoStatusLabel(photo)}
                  </div>
                )}
                <div
                  className="photo-tile__caption"
                  data-status={photoUiStatusFor(photo.upload_status)}
                >
                  {photoStatusLabel(photo)}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
